// src/components/admin/SectionDesignModal.tsx
"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import {
  X,
  Palette,
  Check,
  CheckCircle2,
  Sparkles,
  Image as ImageIcon,
  Flame,
  Star,
  Award,
  BookOpen,
  HelpCircle,
  MessageSquareQuote,
  Images,
  Mail,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Layers,
  LayoutTemplate,
  Tag,
  Sun,
  Moon,
} from "lucide-react";

import SectionPreviewRenderer from "./design";
import BestSellersPreview from "./design/BestSellersPreview";
import { PRODUCT_CARD_VARIANT_LIST } from "@/components/store/sections/product-cards";
import { CATEGORY_VARIANT_LIST } from "@/components/store/sections/categories";
import { NAVBAR_VARIANT_LIST } from "@/components/store/sections/navbar";
import { HERO_BANNER_VARIANT_LIST } from "@/components/store/sections/hero-banner";
import { WEEKLY_BANNER_VARIANT_LIST } from "@/components/store/sections/weekly-banner";
import { BLOG_VARIANT_LIST } from "@/components/store/sections/blog-posts";
import { FAQ_VARIANT_LIST } from "@/components/store/sections/faqs";
import { TESTIMONIAL_VARIANT_LIST } from "@/components/store/sections/testimonials";
import { GALLERY_VARIANT_LIST } from "@/components/store/sections/gallery";
import { NEWSLETTER_VARIANT_LIST } from "@/components/store/sections/newsletter";

interface SectionDesignModalProps {
  isOpen: boolean;
  onClose: () => void;
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
}

const DEFAULT_DESIGNS: Record<string, string> = {
  navbar: "amazon_style",
  hero_banner: "brush_split",
  categories: "notch_ribbon",
  ai_recommender: "door_hanger",
  featured_products: "motion_3d",
  deals_of_the_day: "timer_urgency",
  wide_banner: "classic_dark",
  best_sellers: "motion_3d",
  blog_posts: "grid_cards",
  faqs: "accordion_clean",
  testimonials: "bubble_chat",
  gallery: "instagram_square",
  newsletter: "gradient_banner",
};

export default function SectionDesignModal({
  isOpen,
  onClose,
  subdomain,
  theme = "dark",
  primaryColor = "#059669",
}: SectionDesignModalProps) {
  const [modalTheme, setModalTheme] = useState<"dark" | "light">(theme);

  useEffect(() => {
    setModalTheme(theme);
  }, [theme]);

  const isLight = modalTheme === "light";

  const [selectedSection, setSelectedSection] = useState("categories");
  const [activeDesigns, setActiveDesigns] = useState<Record<string, string>>(DEFAULT_DESIGNS);
  const [previewVariantId, setPreviewVariantId] = useState("notch_ribbon");
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(false);
  const variantScrollRef = useRef<HTMLDivElement>(null);

  const scrollVariantButtons = (dir: "left" | "right") => {
    if (variantScrollRef.current) {
      variantScrollRef.current.scrollBy({
        left: dir === "left" ? -520 : 520,
        behavior: "smooth",
      });
    }
  };

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const fetchActiveDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const token =
        localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";

      const res = await fetch(getApiUrl(`/api/v1/stores/settings/?subdomain=${subdomain}`), {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const saved = data.section_designs || data.theme_config?.section_designs;
        if (saved && typeof saved === "object") {
          setActiveDesigns(saved);
          if (saved[selectedSection]) {
            setPreviewVariantId(saved[selectedSection]);
          }
        }
      }
    } catch (e) {
      console.error("Error fetching active designs:", e);
    } finally {
      setLoading(false);
    }
  }, [getApiUrl, subdomain, selectedSection]);

  useEffect(() => {
    if (isOpen) {
      fetchActiveDesigns();
    }
  }, [isOpen, fetchActiveDesigns]);

  const handleSelectSection = (sectionKey: string) => {
    setSelectedSection(sectionKey);
    const currentActiveForSection =
      activeDesigns[sectionKey] || DEFAULT_DESIGNS[sectionKey] || "motion_3d";
    setPreviewVariantId(currentActiveForSection);
    if (variantScrollRef.current) {
      variantScrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  };

  const handleApplyDesign = async (variantId: string) => {
    setApplying(true);
    try {
      const token =
        localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";

      const updatedDesigns = {
        ...activeDesigns,
        [selectedSection]: variantId,
      };

      const res = await fetch(getApiUrl(`/api/v1/stores/settings/?subdomain=${subdomain}`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          section_designs: updatedDesigns,
        }),
      });

      if (res.ok) {
        const resData = await res.json();
        const saved = resData.section_designs || updatedDesigns;
        setActiveDesigns(saved);
        setPreviewVariantId(variantId);

        if (typeof window !== "undefined") {
          localStorage.setItem("section_designs", JSON.stringify(saved));
          window.dispatchEvent(new Event("theme_settings_updated"));
        }

        Swal.fire({
          icon: "success",
          title: "ডিজাইন স্টোরে সক্রিয় হয়েছে!",
          text: "আপনার নির্বাচিত ডিজাইনটি লাইভ স্টোরফ্রন্টে সেট করা হয়েছে।",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "ব্যর্থ হয়েছে",
          text: "ডিজাইন আপডেট করা যায়নি।",
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "সার্ভার এরর",
        text: "সার্ভারের সাথে যোগাযোগ করা সম্ভব হয়নি।",
      });
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  const SECTIONS_NAV = [
    { key: "navbar", title: "0. Header & Navbar", icon: LayoutTemplate },
    { key: "hero_banner", title: "1. Hero Banner", icon: ImageIcon },
    { key: "categories", title: "2. Categories", icon: Layers },
    { key: "ai_recommender", title: "3. AI Recommender", icon: Sparkles },
    { key: "featured_products", title: "4. Featured Products", icon: Star },
    { key: "deals_of_the_day", title: "5. Deals of the Day", icon: Flame },
    { key: "wide_banner", title: "6. Weekly Deals Banner", icon: Tag },
    { key: "best_sellers", title: "7. Best Sellers", icon: Award },
    { key: "blog_posts", title: "8. Store Blogs", icon: BookOpen },
    { key: "faqs", title: "9. Customer FAQs", icon: HelpCircle },
    { key: "testimonials", title: "10. Testimonials", icon: MessageSquareQuote },
    { key: "gallery", title: "11. Media Gallery", icon: Images },
    { key: "newsletter", title: "12. Newsletter", icon: Mail },
  ];

  const currentVariants =
    selectedSection === "navbar"
      ? NAVBAR_VARIANT_LIST
      : selectedSection === "hero_banner"
      ? HERO_BANNER_VARIANT_LIST
      : selectedSection === "categories"
      ? CATEGORY_VARIANT_LIST
      : selectedSection === "ai_recommender" ||
        selectedSection === "featured_products" ||
        selectedSection === "best_sellers" ||
        selectedSection === "deals_of_the_day"
      ? PRODUCT_CARD_VARIANT_LIST
      : selectedSection === "wide_banner" || selectedSection === "weekly_banner"
      ? WEEKLY_BANNER_VARIANT_LIST
      : selectedSection === "blog_posts"
      ? BLOG_VARIANT_LIST
      : selectedSection === "faqs"
      ? FAQ_VARIANT_LIST
      : selectedSection === "testimonials"
      ? TESTIMONIAL_VARIANT_LIST
      : selectedSection === "gallery"
      ? GALLERY_VARIANT_LIST
      : selectedSection === "newsletter"
      ? NEWSLETTER_VARIANT_LIST
      : PRODUCT_CARD_VARIANT_LIST;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 bg-slate-950/75 backdrop-blur-md animate-in fade-in select-none font-sans">
      <div
        className={`w-full max-w-6xl h-[88vh] rounded-3xl border-2 shadow-2xl flex flex-col overflow-hidden transition-all ${
          isLight
            ? "bg-white border-slate-300 text-slate-900"
            : "bg-slate-900 border-slate-800 text-white"
        }`}
      >
        {/* Top Header */}
        <div
          className={`px-6 py-4 border-b-2 flex items-center justify-between gap-3 shrink-0 ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
              className="w-10 h-10 rounded-2xl flex items-center justify-center border border-emerald-500/30 shrink-0"
            >
              <Palette className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3
                  className={`text-base sm:text-lg font-black tracking-tight ${
                    isLight ? "text-slate-900" : "text-white"
                  }`}
                >
                  Storefront Design Studio — Live UI Preview (13 Sections × 105 Unique Designs)
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 font-mono">
                  1,365 Total Templates
                </span>
              </div>
              <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                যেকোনো সেকশনের ১০৫+ ইউনিক ডিজাইন ডানে-বামে স্ক্রল করে ডেমো দেখুন এবং স্টোরে সক্রিয় করুন।
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Modal Light / Dark Mode Switcher */}
            <div
              className={`inline-flex items-center p-1 rounded-xl border ${
                isLight
                  ? "bg-slate-100 border-slate-200 text-slate-600"
                  : "bg-slate-900 border-slate-800 text-slate-400"
              }`}
            >
              <button
                type="button"
                onClick={() => setModalTheme("light")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                  isLight
                    ? "bg-white text-amber-600 shadow-xs font-black"
                    : "hover:text-slate-200"
                }`}
                title="Switch Studio to Light Mode"
              >
                <Sun className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Light</span>
              </button>
              <button
                type="button"
                onClick={() => setModalTheme("dark")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                  !isLight
                    ? "bg-slate-800 text-emerald-400 shadow-xs font-black"
                    : "hover:text-slate-900"
                }`}
                title="Switch Studio to Dark Mode"
              >
                <Moon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Dark</span>
              </button>
            </div>

            <button
              onClick={onClose}
              className={`p-2 rounded-xl border transition-colors cursor-pointer ${
                isLight
                  ? "bg-white border-slate-300 text-slate-600 hover:bg-slate-100"
                  : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body Grid */}
        <div
          className={`flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x-2 ${
            isLight ? "divide-slate-200" : "divide-slate-800"
          }`}
        >
          {/* Left Column: Sections Sidebar List */}
          <div
            className={`md:col-span-3 p-4 overflow-y-auto space-y-1.5 [scrollbar-width:thin] ${
              isLight ? "bg-slate-50/50" : "bg-slate-950/40"
            }`}
          >
            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider px-2">
              Storefront Sections ({SECTIONS_NAV.length})
            </span>

            {SECTIONS_NAV.map((sec) => {
              const Icon = sec.icon;
              const isSelected = selectedSection === sec.key;
              return (
                <button
                  key={sec.key}
                  onClick={() => handleSelectSection(sec.key)}
                  className={`w-full p-3 rounded-2xl text-left transition-all flex items-center justify-between cursor-pointer border-2 ${
                    isSelected
                      ? "bg-emerald-500/10 text-emerald-500 border-emerald-500 font-black shadow-sm"
                      : isLight
                      ? "bg-white hover:bg-slate-100 border-slate-200 text-slate-700"
                      : "bg-slate-900/60 hover:bg-slate-800/60 border-slate-800 text-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-xs truncate">{sec.title}</span>
                  </div>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-bold shrink-0">
                    105
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right Column: Horizontally Scrollable Variants Selector & Live UI Preview */}
          <div className="md:col-span-9 p-5 flex flex-col min-h-0 overflow-y-auto space-y-5 [scrollbar-width:thin]">
            <div className="space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-xs font-black uppercase tracking-wider ${
                    isLight ? "text-slate-500" : "text-slate-400"
                  }`}
                >
                  Choose UI Layout Template ({currentVariants.length} Unique Variants — Scroll Left/Right)
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => scrollVariantButtons("left")}
                    className={`w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all active:scale-95 cursor-pointer ${
                      isLight
                        ? "bg-white border-slate-200 text-slate-700 hover:border-emerald-500"
                        : "bg-slate-900 border-slate-700 text-slate-200 hover:border-emerald-500"
                    }`}
                    title="Scroll Variants Left"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollVariantButtons("right")}
                    className={`w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all active:scale-95 cursor-pointer ${
                      isLight
                        ? "bg-white border-slate-200 text-slate-700 hover:border-emerald-500"
                        : "bg-slate-900 border-slate-700 text-slate-200 hover:border-emerald-500"
                    }`}
                    title="Scroll Variants Right"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Horizontally Scrollable Variant Selector Buttons */}
              <div
                ref={variantScrollRef}
                className="flex items-stretch gap-3 overflow-x-auto pb-2.5 pt-1 px-0.5 [scrollbar-width:thin] scroll-smooth"
              >
                {currentVariants.map((v: any) => {
                  const isPreviewing = previewVariantId === v.id;
                  const isCurrentlyLive = activeDesigns[selectedSection] === v.id;

                  return (
                    <div
                      key={v.id}
                      onClick={() => setPreviewVariantId(v.id)}
                      className={`w-60 shrink-0 p-3 rounded-2xl border-2 transition-all cursor-pointer relative select-none flex flex-col justify-between min-h-[92px] ${
                        isPreviewing
                          ? "border-emerald-500 bg-emerald-500/10 shadow-md"
                          : isLight
                          ? "border-slate-200 hover:border-slate-300 bg-white"
                          : "border-slate-800 hover:border-slate-700 bg-slate-950/60"
                      }`}
                    >
                      {isCurrentlyLive && (
                        <span className="absolute top-2 right-2 flex items-center gap-0.5 text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full bg-emerald-500 text-white shadow-xs">
                          <Check className="w-2.5 h-2.5" /> Live
                        </span>
                      )}

                      <div>
                        <h4
                          className={`text-xs font-black truncate pr-8 ${
                            isPreviewing
                              ? isLight
                                ? "text-emerald-700"
                                : "text-emerald-400"
                              : isLight
                              ? "text-slate-900"
                              : "text-white"
                          }`}
                        >
                          {v.name}
                        </h4>
                        <p
                          className={`text-[10px] mt-1 line-clamp-2 leading-tight ${
                            isLight ? "text-slate-500" : "text-slate-400"
                          }`}
                        >
                          {v.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modular Live Visual Preview Screen */}
            <div
              className={`p-5 sm:p-6 rounded-3xl border-2 flex-1 flex flex-col justify-between space-y-5 ${
                isLight ? "bg-slate-50/70 border-slate-200" : "bg-slate-950/70 border-slate-800"
              }`}
            >
              <div
                className={`flex items-center justify-between pb-3 border-b ${
                  isLight ? "border-slate-200" : "border-slate-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span
                    className={`text-xs font-black uppercase tracking-wider ${
                      isLight ? "text-slate-600" : "text-slate-400"
                    }`}
                  >
                    Live UI Demo Preview Screen
                  </span>
                </div>

                <button
                  type="button"
                  disabled={applying || loading}
                  onClick={() => handleApplyDesign(previewVariantId)}
                  style={{ backgroundColor: primaryColor }}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-xs font-black shadow-lg hover:opacity-90 active:scale-95 transition-all cursor-pointer"
                >
                  {applying ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  )}
                  <span>
                    {activeDesigns[selectedSection] === previewVariantId
                      ? "Currently Active"
                      : "Set As Active Design"}
                  </span>
                </button>
              </div>

              <div className="flex-1 flex items-center justify-center w-full">
                {selectedSection === "best_sellers" ? (
                  <BestSellersPreview
                    variantId={previewVariantId}
                    primaryColor={primaryColor}
                    theme={modalTheme}
                    subdomain={subdomain}
                  />
                ) : (
                  <SectionPreviewRenderer
                    sectionKey={selectedSection}
                    variantId={previewVariantId}
                    primaryColor={primaryColor}
                    theme={modalTheme}
                    subdomain={subdomain}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}